import React, { useContext } from 'react';
import Loader from '../../components/Loader';
import {T} from '../../utils/I18n/I18n';
import {useTranslation} from "react-i18next";
import { NetworkStatus } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { usePopup } from '../../context/PopupContext';
import Button from '../../components/UI/Button/Button';
import FormInput from '../../components/UI/FormInput/FormInput';
import SocialAuth from '../../components/UI/SocialAuth/SocialAuth';
import { useUserContext } from '../../context/UserContext';
import './Login.scss';

const Login = () => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm();
  const { user, loading, logIn, userStatus } = useUserContext();
  const location = useLocation();
  const { showAddToCartPopupMessage } = usePopup();
  const { t } = useTranslation();

  const userIsLoading = userStatus === NetworkStatus.loading;

  // Przekierowanie dla zalogowanego użytkownika
  if (user) {
    const searchParams = new URLSearchParams(location.search);
    return <Navigate to={searchParams.get('referrer') || '/customer/account'} replace />;
  }

  const onSubmit = async (data) => {
    const { email, password } = data;
    try {
      // Zakładamy, że logIn zwraca obiekt z tokenem
      const response = await logIn(email, password).catch(() => {
        showAddToCartPopupMessage(t('login.wrongPassword'), 'error', 4000);
        throw new Error('Login failed'); // Rzucamy błąd, aby przejść do catch
      });

      // Sprawdzamy, czy odpowiedź zawiera token
      const token = response?.data?.generateCustomerTokenLogin?.token;

      // Obsługa React Native WebView
      setTimeout(() => {
        if (/Android|iPad|iPhone|iPod/i.test(navigator.userAgent) && token) {
          window?.ReactNativeWebView?.postMessage(token);
        }
      }, 0);
    } catch (error) {
      setError('auth', {
        type: 'notMatch',
        message: error.graphQLErrors?.[0]?.message || 'Authentication failed'
      });
    }
  };

  return (
      <div className="customer">
        <div className="login-wrapper">
          <div className="login">
            <form onSubmit={handleSubmit(onSubmit)} className="form">
              <legend className="form__legend">
                <T id="login.existingCustomer" />
              </legend>

              <FormInput
                  register={register}
                  errors={errors}
                  text={<T id="login.email" />}
                  type="email"
                  name="email"
                  required
              />

              <FormInput
                  register={register}
                  errors={errors}
                  text={<T id="login.password" />}
                  type="password"
                  name="password"
                  required
              />

              <Link
                  to="/customer/account/forgotpassword/"
                  className="password-forgot"
              >
                <T id="login.forgotPassword" />
              </Link>

              {loading ? (
                  <Loader height="xxxl" />
              ) : (
                  <Button
                      trait="hover-submit"
                      text={<T id="login.login" />}
                      type="submit"
                  />
              )}

              {!userIsLoading && <SocialAuth />}
            </form>
          </div>
        </div>

        <div className="new-customer-wrapper">
          <div className="new-customer">
            <form className="form">
              <legend className="form__legend">
                <T id="login.newCustomer" />
              </legend>

              <div className="new-customer__text">
                <p>
                  <T id="login.accountMessage" />
                </p>
              </div>

              <ul className="new-customer__benefits">
                {['benefit1', 'benefit2', 'benefit3', 'benefit4'].map((benefit) => (
                    <li key={benefit} className="new-customer__benefit">
                      <T id={`login.${benefit}`} />
                    </li>
                ))}
              </ul>

              <Link to="/customer/registration/" className="new-customer__create">
                <Button
                    trait="hover-submit"
                    text={<T id="login.createAccount" />}
                />
              </Link>
            </form>
          </div>
        </div>
      </div>
  );
};

export default React.memo(Login);